/**
 * "Score recorded outputs" (`/experiments?import=1`): no agent is called. Pick the dataset version the
 * outputs answer, use the outputs saved in it or upload them as JSON or CSV keyed by item, check the preview
 * (keys this version does not have are flagged), pick evaluators, and import.
 * The server matches the records and scores them as a new experiment
 * (`runner: "dataset_output"`); it rejects unknown keys with a 422 that names them.
 */

import { useState } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { SidePanel } from "@/components/ui/overlay";
import { QueryState, errorText } from "@/components/ui/states";
import { useAllDatasetItems, useDatasets } from "@/features/datasets/hooks";
import { useEvaluators } from "@/features/evaluators/hooks";
import { readFileText } from "@/lib/csv";
import { plural } from "@/lib/format";
import { useUiStore } from "@/stores/ui-store";
import { EvaluatorPicker } from "../scoring/evaluator-picker";
import { useDatasetVersions, useExperiments, useImportRecorded } from "../hooks";
import { itemRefs, parseRecordedFile, type ParsedRecord } from "./recorded-file";
import { RecordedPreview } from "./recorded-preview";
import { RecordedSource, type OutputSource } from "./recorded-source";
import { rescoreChoices } from "../scoring/rescore-plan";
import { defaultDataset, defaultSelection } from "../new/use-experiment-form";
import { Select } from "@/components/ui/select";

export function RecordedPanel({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [pickedDataset, setDataset] = useState<string | null>(null);
  const [pickedVersion, setVersion] = useState<number | null>(null);
  const [file, setFile] = useState<{ name: string; records: ParsedRecord[] } | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [pickedSource, setSource] = useState<OutputSource | null>(null);

  const datasets = useDatasets();
  const recent = useExperiments();
  const datasetId = pickedDataset ?? defaultDataset(datasets.data ?? [], recent.data ?? []);
  const detail = useDatasetVersions(datasetId);
  const versions = detail.data?.versions ?? [];
  const version =
    pickedVersion ??
    detail.data?.latest_version?.version ??
    datasets.data?.find((d) => d.id === datasetId)?.latest_version?.version ??
    null;
  const items = useAllDatasetItems(datasetId, version);
  const hasDatasetOutputs = (items.data ?? []).some((it) => it.output !== null && it.output !== undefined);
  const source = pickedSource ?? (hasDatasetOutputs ? "dataset" : "file");
  const refs = itemRefs(items.data ?? []);
  const evaluators = useEvaluators({ dataset_id: datasetId ?? undefined });
  const list = evaluators.data ?? [];
  const selected = new Set(
    [...(picked ?? defaultSelection(list))].filter((id) => list.some((e) => e.id === id && e.compatible)),
  );

  const importRecorded = useImportRecorded();
  const notify = useUiStore((s) => s.notify);
  const navigate = useNavigate();
  const records = file?.records ?? [];
  const blocker = !name.trim()
    ? "Name the experiment."
    : !datasetId
      ? "Pick a dataset."
      : source === "file" && records.length === 0
        ? "Upload the outputs."
        : selected.size === 0
          ? "Pick at least one evaluator."
          : null;

  const pickFile = async (f: File | undefined) => {
    if (!f) return;
    const parsed = parseRecordedFile(f.name, await readFileText(f));
    if (typeof parsed === "string") (setFile(null), setFileError(parsed));
    else (setFile({ name: f.name, records: parsed }), setFileError(null));
  };
  const flip = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setPicked(next);
  };
  const submit = () => {
    if (!datasetId) return;
    const chosen = list.filter((e) => selected.has(e.id));
    importRecorded.mutate(
      {
        name: name.trim(),
        dataset_id: datasetId,
        ...(version !== null ? { dataset_version: version } : {}),
        evaluators: rescoreChoices([], new Set(), new Map(), chosen),
        ...(source === "dataset" ? { from_dataset: true } : { records: records.map((r) => r.record) }),
      },
      {
        onSuccess: (e) => (
          notify(
            source === "dataset"
              ? "Scoring the dataset outputs now."
              : `Imported ${plural(records.length, "output")}. Scoring now.`,
          ),
          navigate(`/experiments/${e.id}`)
        ),
        onError: (err) => notify(errorText(err, "Could not import the outputs."), "error"),
      },
    );
  };

  return (
    <SidePanel
      title="Score recorded outputs"
      subtitle="Scores outputs you already have. No agent is called."
      onClose={onClose}
      footer={
        <>
          {blocker && (
            <span id="rec-blocker" className="text-xs text-muted">
              {blocker}
            </span>
          )}
          <span className="flex-1" />
          <Button
            variant="primary"
            disabled={!!blocker || importRecorded.isPending}
            aria-describedby={blocker ? "rec-blocker" : undefined}
            onClick={submit}
          >
            Import and score
          </Button>
        </>
      }
    >
      <Field
        label="What are you testing?"
        required
        htmlFor="rec-name"
        hint="This becomes the experiment's name."
      >
        <input
          id="rec-name"
          className="inp w-full"
          value={name}
          placeholder="e.g. Vendor model outputs, March"
          onChange={(e) => setName(e.target.value)}
        />
      </Field>
      <div className="grid grid-cols-[minmax(0,1fr)_140px] gap-4">
        <Field label="Dataset" required htmlFor="rec-ds">
          <Select
            id="rec-ds"
            value={datasetId ?? ""}
            onChange={(id) => (setDataset(id), setVersion(null))}
            placeholder="Pick a dataset"
            options={(datasets.data ?? []).map((d) => ({ value: d.id, label: d.name }))}
          />
        </Field>
        <Field label="Version" htmlFor="rec-ver">
          <Select
            id="rec-ver"
            value={version === null ? "" : String(version)}
            onChange={(v) => setVersion(Number(v))}
            placeholder=""
            options={[
              ...(versions.length === 0 && version !== null
                ? [{ value: String(version), label: `v${version}` }]
                : []),
              ...versions.map((v) => ({
                value: String(v.version),
                label: `v${v.version} · ${plural(v.item_count, "item")}`,
              })),
            ]}
          />
        </Field>
      </div>
      <RecordedSource
        source={source}
        hasDatasetOutputs={hasDatasetOutputs}
        onSource={setSource}
        file={file?.name}
        fileError={fileError}
        onFile={(f) => void pickFile(f)}
      />
      {source === "file" && file && (
        <RecordedPreview fileName={file.name} records={records} known={items.data ? refs : null} />
      )}
      <Field label="Evaluators" aside={`${selected.size} selected`}>
        <QueryState query={evaluators} what="evaluators">
          <EvaluatorPicker evaluators={list} selected={selected} onToggle={flip} />
        </QueryState>
      </Field>
    </SidePanel>
  );
}
